import { NotificationData } from '../../../services/notification.service';
import { formatWayActiveMembershipDisplayName } from '../../../shared/utils/notification-display.util';
import { WayNormalizedPurchase } from '../models/way-purchase.models';
import {
  isRedeemableWayPurchase,
  WAY_SUBSCRIPTION_ALREADY_REDEEMED_NOTICE,
} from './way-purchase-lookup-orchestration.util';
import { pickString, readPurchase, readRecord } from './way-purchase-display.helpers';
import { readPurchaseOptions } from './way-purchase-selection-display.util';

export type WayPurchaseLayoutPurchaseType = 'single_wash' | 'membership' | 'other';

export type WayPurchaseUnavailableKind = 'already_redeemed' | 'unredeemable';

export { WAY_SUBSCRIPTION_ALREADY_REDEEMED_NOTICE };

export interface WayPurchaseDetailView {
  data: Record<string, any>;
  isUnavailable: boolean;
  unavailableKind: WayPurchaseUnavailableKind | null;
  purchaseType: WayPurchaseLayoutPurchaseType | null;
}

function isAlreadyRedeemedTodayMembership(
  purchase: WayNormalizedPurchase | null | undefined
): boolean {
  if (!purchase) {
    return false;
  }
  const purchaseType = String(purchase.purchase_type ?? '')
    .trim()
    .toLowerCase();
  return (
    purchaseType === 'membership' &&
    purchase.can_redeem === false &&
    purchase.redeemed_today === true
  );
}

function resolveUnavailableKind(
  purchase: WayNormalizedPurchase | null | undefined,
  isUnavailable: boolean
): WayPurchaseUnavailableKind | null {
  if (!isUnavailable) {
    return null;
  }
  return isAlreadyRedeemedTodayMembership(purchase) ? 'already_redeemed' : 'unredeemable';
}

export function resolveWayPurchaseLayoutPurchaseType(
  data: Record<string, any> | null | undefined
): WayPurchaseLayoutPurchaseType | null {
  const purchase = readPurchase(readRecord(data));
  const normalized = String(purchase?.purchase_type ?? '')
    .trim()
    .toLowerCase();
  if (normalized === 'single_wash') {
    return 'single_wash';
  }
  if (normalized === 'membership') {
    return 'membership';
  }
  if (normalized) {
    return 'other';
  }
  return null;
}

function resolveSingleUnredeemableWayPurchase(
  row: Record<string, any>
): WayNormalizedPurchase | null {
  const options = readPurchaseOptions(row);
  if (options.length !== 1) {
    return null;
  }
  const [option] = options;
  if (option.source !== 'way') {
    return null;
  }
  const purchase = option.purchase;
  if (!purchase || isRedeemableWayPurchase(purchase)) {
    return null;
  }
  return purchase;
}

/**
 * Resolves the single-purchase detail view used by redeemable and unredeemable modals.
 * Also covers `way-purchase-selection` when there is exactly one unredeemable Way option.
 */
export function resolveWayPurchaseDetailView(
  notification: NotificationData | null | undefined
): WayPurchaseDetailView | null {
  if (!notification?.data) {
    return null;
  }

  const row = readRecord(notification.data);
  if (!row) {
    return null;
  }

  if (notification.type === 'way-single-wash' || notification.type === 'way-active-subscription') {
    const purchase = readPurchase(row);
    if (!purchase) {
      return null;
    }
    const isUnavailable = !isRedeemableWayPurchase(purchase);
    return {
      data: row,
      isUnavailable,
      unavailableKind: resolveUnavailableKind(purchase, isUnavailable),
      purchaseType: resolveWayPurchaseLayoutPurchaseType(row),
    };
  }

  if (
    notification.type === 'way-purchase-unredeemable' ||
    notification.type === 'way-subscription-already-redeemed-today'
  ) {
    const purchase = readPurchase(row);
    if (!purchase) {
      return null;
    }
    return {
      data: row,
      isUnavailable: true,
      unavailableKind: resolveUnavailableKind(purchase, true),
      purchaseType: resolveWayPurchaseLayoutPurchaseType(row),
    };
  }

  if (notification.type === 'way-purchase-selection') {
    const purchase = resolveSingleUnredeemableWayPurchase(row);
    if (!purchase) {
      return null;
    }
    return {
      data: {
        ...row,
        purchase,
        reason: pickString(row['reason'], purchase.unredeemable_reason),
      },
      isUnavailable: true,
      unavailableKind: resolveUnavailableKind(purchase, true),
      purchaseType: resolveWayPurchaseLayoutPurchaseType({ purchase }),
    };
  }

  return null;
}

export function isWayPurchaseUnredeemableDetailView(
  notification: NotificationData | null | undefined
): boolean {
  return resolveWayPurchaseDetailView(notification)?.isUnavailable === true;
}

export function shouldShowWayPurchaseSingleWashLayout(
  notification: NotificationData | null | undefined
): boolean {
  const detail = resolveWayPurchaseDetailView(notification);
  return detail?.purchaseType === 'single_wash';
}

export function shouldShowWayPurchaseActiveSubscriptionLayout(
  notification: NotificationData | null | undefined
): boolean {
  const detail = resolveWayPurchaseDetailView(notification);
  return detail?.purchaseType === 'membership';
}

export function shouldShowWayPurchaseSelectionLayout(
  notification: NotificationData | null | undefined
): boolean {
  return (
    notification?.type === 'way-purchase-selection' &&
    !isWayPurchaseUnredeemableDetailView(notification)
  );
}

export function resolveWayPurchaseDetailTitle(
  notification: NotificationData | null | undefined
): string {
  const detail = resolveWayPurchaseDetailView(notification);
  if (!detail) {
    return 'Way purchase';
  }

  if (detail.isUnavailable) {
    if (detail.purchaseType === 'single_wash') {
      return 'Single Wash - Unredeemable';
    }
    if (detail.purchaseType === 'membership') {
      if (detail.unavailableKind === 'already_redeemed') {
        return 'Subscription Already Redeemed';
      }
      return 'Subscription - Unredeemable';
    }
    return 'Way purchase unavailable';
  }

  if (detail.purchaseType === 'membership') {
    return formatWayActiveMembershipDisplayName(detail.data);
  }
  if (detail.purchaseType === 'single_wash') {
    return 'Single Wash';
  }
  return 'Way purchase';
}

function isPurchaseOptionUsable(option: ReturnType<typeof readPurchaseOptions>[number]): boolean {
  if (option.source === 'shop') {
    return (
      String(option.order_status ?? '')
        .trim()
        .toLowerCase() !== 'cancelled'
    );
  }
  if (option.source === 'way') {
    return isRedeemableWayPurchase(option.purchase);
  }
  return false;
}

export function resolveWayPurchaseSelectionModalTitle(
  notification: NotificationData | null | undefined
): string {
  const options = readPurchaseOptions(readRecord(notification?.data));
  if (!options.length) {
    return 'Select Purchase';
  }
  return options.some(isPurchaseOptionUsable) ? 'Select Purchase' : 'Unredeemable Purchases';
}

export function resolveWayPurchaseUnredeemableReason(
  data: Record<string, any> | null | undefined,
  fallbackMessage = 'This Way purchase cannot be redeemed.'
): string {
  const row = readRecord(data);
  const purchase = readPurchase(row);
  if (isAlreadyRedeemedTodayMembership(purchase)) {
    return WAY_SUBSCRIPTION_ALREADY_REDEEMED_NOTICE;
  }
  return pickString(row?.['reason'], purchase?.unredeemable_reason) || fallbackMessage;
}

/** Data shape for unredeemable dismiss → create-order handoff. */
export function resolveWayPurchaseUnredeemableHandoffData(
  notification: NotificationData | null | undefined
): Record<string, any> | null {
  const detail = resolveWayPurchaseDetailView(notification);
  if (!detail?.isUnavailable) {
    return readRecord(notification?.data);
  }
  return detail.data;
}
